import { InstructorInfo } from '@interfaces/Instructor'
import { SessionInfo } from '@layouts/Scheduling/ScheduleListCard/interface'
import { find, flattenDeep, groupBy, isEmpty, values } from 'lodash'
import moment from 'moment'
import PortalPrograms from './Programs'

export interface SurveyRecipients {
  [key: string]: SessionInfo[]
}

export interface SatisfactionSurveyInput extends SessionInfo {
  LanguageInstructorId?: string
}

export interface SurveyInstructor {
  NameWithLastInitial?: string
  FirstName: string
  LastName: string
  Id: string
}

class SatisfactionSurvey {
  data: SatisfactionSurveyInput[]
  studentPrograms: PortalPrograms

  constructor(data: SatisfactionSurveyInput[], studentPrograms: PortalPrograms) {
    this.studentPrograms = studentPrograms
    this.data = data.map(session => {
      session.LanguageInstructorId = `${session.Language}_${session.InstructorId}`
      session.Description = studentPrograms.isProgramFlex(session.ProgramId) ? undefined : session.Description
      return session
    })
  }

  SurveyFormGoal(id: string) {
    return this.SurveyRecipients?.[id]
      ?.map(lesson => lesson.Description || null)
      .filter(Boolean)
      .reverse()
  }

  IsFeedbackEnabled(id: string) {
    return !isEmpty(find(this.SessionsWithFeedback, session => session.Id === id))
  }

  ConvertToSurveyInstructor(instructors: InstructorInfo[]) {
    return instructors.map(instructor => {
      return {
        NameWithLastInitial: `${instructor.contactInfo?.FirstName} ${instructor.contactInfo?.LastName?.charAt(
          0
        ).toLocaleUpperCase()}.`,
        FirstName: instructor.contactInfo?.FirstName,
        LastName: instructor.contactInfo?.LastName,
        Id: instructor.Id,
      }
    }) as SurveyInstructor[]
  }

  GetInstructorFeedbackName(data: InstructorInfo[] = [], instructorId: string = '') {
    const instructors = this.ConvertToSurveyInstructor(data)
    const instructor = find(instructors, item => item.Id === instructorId)
    const fullName = `${instructor?.FirstName} ${instructor?.LastName}`
    const hasSimilar = (field: string, name = '') => instructors.filter(item => item?.[field] === name)?.length > 1

    return hasSimilar('FirstName', instructor?.FirstName)
      ? hasSimilar('NameWithLastInitial', instructor?.NameWithLastInitial)
        ? fullName
        : instructor?.NameWithLastInitial
      : instructor?.FirstName
  }

  get SurveyRecipients(): SurveyRecipients {
    const sortByTimeDesc = (d1, d2) => new Date(d2?.EndTime || '').getTime() - new Date(d1?.EndTime || '').getTime()
    const sortByStartTimeAsc = (d1, d2) =>
      new Date(d1?.StartTime || '').getTime() - new Date(d2?.StartTime || '').getTime()

    const filteredData = this.data
      ?.filter(
        item =>
          item.InstructorId &&
          item.Status !== 'Cancelled' &&
          !item.FeedbackId &&
          item.LessonRegistered &&
          item.Attendance !== 'Absent'
      )
      .sort(sortByTimeDesc)
    // check the last lesson of the day's end time if it has passed the 30min. mark
    const isLastLessonDone = moment(filteredData[0]?.EndTime)
      .add(30, 'minutes')
      .isSameOrBefore(moment())
    const isNotPast2Weeks = moment().isSameOrBefore(moment(filteredData[0]?.EndTime).add(14, 'days'), 'days')
    const data = isLastLessonDone && isNotPast2Weeks ? filteredData : []

    const dataByLang = groupBy(data, session => session.Language)

    Object.keys(dataByLang).map(key => {
      const grpInstructorLang = groupBy(dataByLang[key], session => session.LanguageInstructorId)
      // sort each grouped instructor_language lessons in asc order
      Object.keys(grpInstructorLang).map(k => (grpInstructorLang[k] = grpInstructorLang[k].sort(sortByStartTimeAsc)))
      // sort grouped instructor_language by their lesson's start time
      const sorted = Object.entries(grpInstructorLang)
        .sort((a, b) => sortByStartTimeAsc(a[1][0], b[1][0]))
        .map(item => item[1])
      dataByLang[key] = flattenDeep(sorted)
    })

    // sort language group by their first lesson's start time
    const sortedByLangStart = Object.entries(dataByLang)
      .sort((a, b) => sortByStartTimeAsc(a[1][0], b[1][0]))
      .map(item => item[1])

    // group by languageinstructor id again
    return groupBy(flattenDeep(sortedByLangStart), session => session.LanguageInstructorId)
  }

  get SessionsWithFeedback(): SessionInfo[] {
    return flattenDeep(values(this.SurveyRecipients))
  }
}

export default SatisfactionSurvey
